import { qrcodegen } from './vendor/qrcodegen';
import { documentToPlainTextString } from './contentful';

const { QrCode } = qrcodegen;

// Track Blob URL to revoke on re-init
let currentBlobUrl = null;
// Track QR popover element
let qrPopover = null;
let outsideClickBound = false;

const getPlainText = (richText) => {
  if (!richText) return '';
  return documentToPlainTextString(richText).trim();
};

/**
 * Extract raw value (e.g. phone number digits, email address) from RichText
 * that may contain a hyperlink. Falls back to plain text extraction.
 */
const extractRawValue = (richText, type) => {
  if (!richText) return '';

  const findHyperlink = (node) => {
    if (node.nodeType === 'hyperlink' && node.data?.uri) {
      return node.data.uri;
    }
    if (node.content) {
      for (const child of node.content) {
        const found = findHyperlink(child);
        if (found) return found;
      }
    }
    return null;
  };

  const uri = findHyperlink(richText);

  if (uri) {
    if (type === 'email' && uri.startsWith('mailto:')) {
      return uri.replace('mailto:', '');
    }
    if (type === 'phone' && uri.startsWith('tel:')) {
      return uri.replace('tel:', '');
    }
    if (type === 'url') {
      return uri;
    }
  }

  return getPlainText(richText);
};

const buildVCard = (contact) => {
  const lines = ['BEGIN:VCARD', 'VERSION:3.0'];

  if (contact.name) {
    const nameParts = contact.name.split(' ');
    const lastName = nameParts.pop() || '';
    const firstName = nameParts.join(' ') || '';
    lines.push(`N:${lastName};${firstName};;;`);
    lines.push(`FN:${contact.name}`);
  }
  if (contact.org) lines.push(`ORG:${contact.org}`);
  if (contact.email) lines.push(`EMAIL:${contact.email}`);
  if (contact.phone) lines.push(`TEL:${contact.phone}`);
  if (contact.website) lines.push(`URL:${contact.website}`);
  if (contact.linktree) lines.push(`URL:${contact.linktree}`);
  if (contact.address) {
    // vCard ADR: PO Box;Extended;Street;City;Region;Postal Code;Country
    const escaped = contact.address.replace(/\n/g, '\\n').replace(/,/g, '\\,');
    lines.push(`ADR:;;${escaped};;;;`);
  }

  lines.push('END:VCARD');
  return lines.join('\r\n');
};

// Scanners want dark modules on a light quiet zone, so the code is always
// black on white no matter which theme or page background is behind it.
const renderQrSvg = (data) => {
  const qr = QrCode.encodeText(data, QrCode.Ecc.LOW);
  const size = qr.size;
  const border = 3;
  const scale = 4;
  const totalSize = (size + border * 2) * scale;

  let pathData = '';
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (qr.getModule(x, y)) {
        const px = (x + border) * scale;
        const py = (y + border) * scale;
        pathData += `M${px},${py}h${scale}v${scale}h-${scale}z`;
      }
    }
  }

  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('viewBox', `0 0 ${totalSize} ${totalSize}`);
  svg.setAttribute('class', 'qr-code');
  svg.setAttribute('role', 'img');

  const bg = document.createElementNS(ns, 'rect');
  bg.setAttribute('width', '100%');
  bg.setAttribute('height', '100%');
  bg.setAttribute('fill', '#ffffff');
  svg.appendChild(bg);

  const path = document.createElementNS(ns, 'path');
  path.setAttribute('d', pathData);
  path.setAttribute('fill', '#000000');
  svg.appendChild(path);

  return svg;
};

const getLabel = (key) => {
  const lang = document.documentElement.lang || 'en';
  const labels = {
    saveContact: { en: 'Save contact', de: 'Kontakt speichern' },
    qrAlt: {
      en: 'QR code with the contact details of HINTERLAND software',
      de: 'QR-Code mit den Kontaktdaten von HINTERLAND software',
    },
    showQr: { en: 'Show contact QR code', de: 'Kontakt-QR-Code anzeigen' },
    hideQr: { en: 'Hide contact QR code', de: 'Kontakt-QR-Code ausblenden' },
  };
  return labels[key]?.[lang] || labels[key]?.en || key;
};

export const initBusinessCard = (microcopy) => {
  if (currentBlobUrl) {
    URL.revokeObjectURL(currentBlobUrl);
    currentBlobUrl = null;
  }

  const contact = {
    name: getPlainText(microcopy['contact.name']),
    email: extractRawValue(microcopy['contact.email'], 'email'),
    phone: extractRawValue(microcopy['contact.phone'], 'phone'),
    linktree: extractRawValue(microcopy['contact.linktree'], 'url'),
    address: getPlainText(microcopy['company.address']),
    org: getPlainText(microcopy['company.heading']),
    website: 'https://hinterland.software',
  };

  const slot = document.getElementById('download.business-card');
  if (!slot) {
    console.error('Business card slot not found');
    return;
  }

  const vcard = buildVCard(contact);
  const blob = new Blob([vcard], { type: 'text/vcard' });
  currentBlobUrl = URL.createObjectURL(blob);

  slot.textContent = '';
  const downloadLink = document.createElement('a');
  downloadLink.href = currentBlobUrl;
  downloadLink.download = 'hinterland-software.vcf';
  downloadLink.className = 'business-card-download';
  downloadLink.textContent = getLabel('saveContact');
  slot.appendChild(downloadLink);

  if (!qrPopover) {
    qrPopover = document.createElement('div');
    qrPopover.className = 'qr-popover';
    qrPopover.id = 'qr-popover';
    document.body.appendChild(qrPopover);
  }
  qrPopover.textContent = '';
  const qrSvg = renderQrSvg(vcard);
  qrSvg.setAttribute('aria-label', getLabel('qrAlt'));
  qrPopover.appendChild(qrSvg);
  qrPopover.classList.remove('visible');

  const toggleBtn = document.querySelector('aside button.qrcode');
  if (!toggleBtn) return;

  const setExpanded = (expanded) => {
    qrPopover.classList.toggle('visible', expanded);
    toggleBtn.setAttribute('aria-expanded', String(expanded));
    toggleBtn.setAttribute(
      'aria-label',
      getLabel(expanded ? 'hideQr' : 'showQr')
    );
    toggleBtn.setAttribute('title', getLabel(expanded ? 'hideQr' : 'showQr'));
  };

  toggleBtn.setAttribute('aria-controls', 'qr-popover');
  toggleBtn.onclick = () =>
    setExpanded(!qrPopover.classList.contains('visible'));
  setExpanded(false);

  if (!outsideClickBound) {
    outsideClickBound = true;
    document.addEventListener('click', (e) => {
      if (!qrPopover.contains(e.target) && !toggleBtn.contains(e.target)) {
        setExpanded(false);
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') setExpanded(false);
    });
  }
};
